// Counter Intelligence — natural-language single-item creation helpers.
//
// The classifier extracts item fields from chat text; this module validates,
// merges follow-up details, and builds preview text. It is pure (no React /
// no I/O) so it is unit-testable.

const FIELD_LABELS = {
  name: 'name',
  category: 'category',
  unit: 'unit',
  price: 'price',
  location: 'location',
  vendor: 'vendor',
  pack_size: 'pack size',
  brand: 'brand',
};

const OPTIONAL_FIELDS = ['price', 'location', 'vendor', 'pack_size', 'brand'];

export const ITEM_CREATE_FORM_FIELDS = [
  { key: 'name', label: 'Item name', type: 'text', required: true },
  { key: 'category', label: 'Category', type: 'text', required: true },
  { key: 'unit', label: 'Unit', type: 'text', required: true },
  { key: 'price', label: 'Price', type: 'number', required: false },
  { key: 'location', label: 'Location', type: 'text', required: false },
  { key: 'vendor', label: 'Vendor', type: 'text', required: false },
  { key: 'pack_size', label: 'Pack size', type: 'text', required: false },
  { key: 'brand', label: 'Brand', type: 'text', required: false },
];

export function buildItemCreateFormValues(fields) {
  const f = normalizeItemFields(fields);
  return ITEM_CREATE_FORM_FIELDS.reduce((acc, def) => {
    acc[def.key] = f[def.key] ?? '';
    return acc;
  }, {});
}

function toNum(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

export function normalizeItemFields(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  return {
    name: typeof source.name === 'string' ? source.name.trim() : null,
    category: typeof source.category === 'string' ? source.category.trim() : null,
    unit: typeof source.unit === 'string' ? source.unit.trim() : null,
    price: toNum(source.price),
    location: typeof source.location === 'string' ? source.location.trim() : null,
    vendor: typeof source.vendor === 'string' ? source.vendor.trim() : null,
    pack_size: typeof source.pack_size === 'string' ? source.pack_size.trim() : null,
    brand: typeof source.brand === 'string' ? source.brand.trim() : null,
  };
}

export function validateItemCreate(fields) {
  const f = normalizeItemFields(fields);
  const missing = [];
  if (!f.name) missing.push('name');
  if (!f.category) missing.push('category');
  if (!f.unit) missing.push('unit');
  return { ok: missing.length === 0, missing };
}

export function mergeItemFields(base, update) {
  const a = normalizeItemFields(base);
  const b = normalizeItemFields(update);
  const merged = { ...a };
  Object.keys(b).forEach((key) => {
    if (b[key] != null) merged[key] = b[key];
  });
  return merged;
}

function fmtMoney(v) {
  const n = toNum(v);
  return n != null ? `$${n.toFixed(2)}` : '—';
}

export function buildItemCreatePreview(fields) {
  const f = normalizeItemFields(fields);
  if (!f.name && !f.category && !f.unit) return '';
  const parts = [f.name, f.category].filter(Boolean);
  const details = [];
  if (f.price != null && f.unit) details.push(`${fmtMoney(f.price)}/${f.unit}`);
  else if (f.unit) details.push(f.unit);
  if (f.location) details.push(f.location);
  if (f.vendor) details.push(f.vendor);
  if (f.pack_size) details.push(f.pack_size);
  if (f.brand) details.push(f.brand);
  if (details.length) parts.push(details.join(' · '));
  return parts.join(' · ');
}

export function itemCreateMissingMessage(missing) {
  const labels = missing.map((k) => FIELD_LABELS[k] || k);
  const list = labels.join(', ');
  const verb = missing.length === 1 ? 'is it' : 'are they';
  return `I need the name, category, and unit to add this item correctly. You’re missing: ${list}. What ${verb}?`;
}

export function itemCreateOptionalPrompt(fields) {
  const f = normalizeItemFields(fields);
  const base = `I have ${f.name} · ${f.category} · ${f.unit}.`;
  const present = OPTIONAL_FIELDS.filter((k) => f[k] != null).map((k) => FIELD_LABELS[k]);
  if (!present.length) {
    return `${base} Would you like to add price, location, vendor, pack size, or brand? The more detail I have, the better data you’ll get.`;
  }
  return `${base} I also have ${present.join(', ')}. Would you like to add anything else?`;
}

export function itemCreateOptionalChips() {
  return [
    { key: 'ici_add_price', label: 'Price' },
    { key: 'ici_add_location', label: 'Location' },
    { key: 'ici_add_vendor', label: 'Vendor' },
    { key: 'ici_add_pack_size', label: 'Pack size' },
    { key: 'ici_add_brand', label: 'Brand' },
    { key: 'ici_done', label: 'That’s everything' },
  ];
}

// ── Reusing existing catalog values (category / location) ─────────────────────
// When the assistant proposes a category or location for a new item, it should
// reuse a value the store ALREADY uses rather than minting a near-duplicate —
// the whole point is to avoid "Beer" and "Beers", or "Walk In" and "Walk-In",
// living side by side. These pure helpers fold case, punctuation, and a simple
// trailing plural into a comparison key so an extracted value can be matched
// back to the store's own spelling. (Vendors go through vendorResolution's
// alias-aware directory instead — this is only for the free-text fields.)

// Fold a single trailing plural "s" on a word (not "ss"), for words long enough
// that the fold is safe: "beers" → "beer", "wines" → "wine", "beverages" →
// "beverage"; "glass" and "gas" are left alone.
function foldPluralWord(word) {
  return word.length >= 4 && word.endsWith('s') && !word.endsWith('ss')
    ? word.slice(0, -1)
    : word;
}

// A normalized key for matching one label against another: lowercased, with
// punctuation/whitespace collapsed and each word de-pluralized. Empty in → "".
export function labelMatchKey(value) {
  const base = String(value ?? '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!base) return '';
  return base.split(' ').map(foldPluralWord).join(' ');
}

// De-duplicate a list of labels case/plural-insensitively, keeping the first
// spelling seen and the original order. Blank entries are dropped.
export function dedupeLabels(list) {
  const seen = new Set();
  const out = [];
  (Array.isArray(list) ? list : []).forEach((raw) => {
    const label = String(raw ?? '').trim();
    if (!label) return;
    const key = labelMatchKey(label) || label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(label);
  });
  return out;
}

// Return the existing label (in its own spelling) that matches `value`, or null.
// So findExistingLabel('Beers', ['Beer', 'Wine']) === 'Beer'.
export function findExistingLabel(value, existing) {
  const key = labelMatchKey(value);
  if (!key) return null;
  const list = Array.isArray(existing) ? existing : [];
  for (const candidate of list) {
    if (labelMatchKey(candidate) === key) return String(candidate);
  }
  return null;
}

// Decide how a catalog field (category/location) should be presented in the
// item-create form, given the value the assistant proposed and the store's
// existing values:
//   { options, mode: 'existing'|'new'|'empty', selected, newValue }
// - 'existing' → the proposed value matched one the store already uses; select
//   that canonical spelling (selected), so no duplicate is created.
// - 'new'      → a genuinely new value; default to the add-new input pre-filled
//   with it (newValue), while still letting the user pick an existing option.
// - 'empty'    → nothing proposed; the user chooses.
export function resolveCatalogFieldChoice(value, existing) {
  const options = dedupeLabels(existing);
  const matched = findExistingLabel(value, options);
  if (matched) return { options, mode: 'existing', selected: matched, newValue: '' };
  const raw = String(value ?? '').trim();
  if (raw) return { options, mode: 'new', selected: '', newValue: raw };
  return { options, mode: 'empty', selected: '', newValue: '' };
}
